/**
 * Nơi DUY NHẤT biết phép biến đổi giữa 4 nút trên thanh phương thức nhận hàng và
 * cặp field `receive_method` × `shipping_mode` mà Form/BE dùng.
 *
 * Cố ý KHÔNG import type từ component UI nào (`ReceiveMethodToggle` cũ đã bị xoá
 * đúng theo dự tính này): đây là tầng thấp nhất — không phụ thuộc UI.
 */

export type ReceiveMethodValue = 'delivery' | 'pickup' | 'vault';
export type ShippingModeValue = 'self' | 'carrier';

/** Một nút trên thanh. Làm phẳng hai field thành một lựa chọn loại trừ nhau. */
export type FulfillmentChoice = 'pickup' | 'self' | 'carrier' | 'vault';

export interface FulfillmentFields {
  receive_method: ReceiveMethodValue;
  shipping_mode?: ShippingModeValue;
}

export const FULFILLMENT_CHOICES: { value: FulfillmentChoice; label: string }[] = [
  { value: 'pickup', label: 'Lấy tại chi nhánh' },
  { value: 'self', label: 'Tự vận chuyển' },
  { value: 'carrier', label: 'Giao cho ĐVVC' },
  // [BỎ LƯU KHO] ẩn khỏi UI chọn mới — comment out, KHÔNG xoá, để dữ liệu
  // đơn cũ vẫn hiển thị đúng qua `fromChoice`/`toChoice` (2 hàm đó vẫn xử lý
  // đủ 4 giá trị của type `FulfillmentChoice`, không đổi).
  // { value: 'vault', label: 'Lưu kho' },
];

/**
 * Hai lựa chọn khách nhận hàng tại chi nhánh — dùng chung field `pickup_store_id`
 * (BE đọc đúng một trường cho cả hai, xem `pos-order.service.ts`).
 */
export const CHOICES_NEEDING_BRANCH = ['pickup', 'vault'] as const;

export function fromChoice(choice: FulfillmentChoice): FulfillmentFields {
  switch (choice) {
    case 'pickup':
      return { receive_method: 'pickup', shipping_mode: undefined };
    case 'vault':
      return { receive_method: 'vault', shipping_mode: undefined };
    case 'self':
      return { receive_method: 'delivery', shipping_mode: 'self' };
    case 'carrier':
      return { receive_method: 'delivery', shipping_mode: 'carrier' };
  }
}

/**
 * Đơn giao hàng thiếu `shipping_mode` được đọc là `'self'`.
 *
 * Thanh 4 nút KHÔNG dựng được trạng thái đó, nên nó chỉ đến từ đơn cũ / dữ liệu
 * khôi phục từ localStorage. Trả `undefined` ở đây sẽ để thanh không nút nào sáng
 * — thu ngân không biết đơn đang ở chế độ nào. `'self'` là mặc định an toàn hơn:
 * nó KHÔNG đẩy job tạo vận đơn, khác với `'carrier'`.
 *
 * Fix round 1 (Task 7b): nhánh delivery gọi thẳng `effectiveShippingMode` thay vì
 * viết lại luật "delivery + rỗng ⇒ 'self'" lần hai trong cùng file — cùng một
 * luật, một chỗ định nghĩa duy nhất. `pickup`/`vault` vẫn trả sớm ở trên vì
 * `effectiveShippingMode` luôn trả `undefined` cho hai trường hợp đó (khác nghĩa
 * với "nút nào sáng" mà `toChoice` cần).
 */
export function toChoice(
  { receive_method, shipping_mode }: FulfillmentFields,
  defaultChoice: FulfillmentChoice,
): FulfillmentChoice {
  if (receive_method === 'pickup') return 'pickup';
  if (receive_method === 'vault') return 'vault';
  return effectiveShippingMode(receive_method, shipping_mode, defaultChoice) === 'carrier' ? 'carrier' : 'self';
}

/**
 * `shipping_mode` thực tế của đơn, dùng ở MỌI biên đọc (cổng checkout, dựng
 * payload, hiển thị).
 *
 * Đơn giao hàng thiếu `shipping_mode` được coi là `'self'` — cùng mặc định an
 * toàn mà `toChoice` dùng, vì `'self'` KHÔNG đẩy job tạo vận đơn.
 *
 * Chuẩn hoá ở biên đọc chứ KHÔNG ghi vào Form: đơn cũ mở ở `Orders/edit` không
 * có thanh phương thức nhận hàng để ghi, và một bất biến phụ thuộc thứ tự chạy
 * effect là bất biến sẽ đứt (xem Task 7b — effect chuẩn hoá trong
 * `FulfillmentFormSection` đã bị xoá).
 */
export function effectiveShippingMode(
  receiveMethod: ReceiveMethodValue | undefined,
  shippingMode: ShippingModeValue | undefined,
  defaultChoice: FulfillmentChoice,
): ShippingModeValue | undefined {
  if ((receiveMethod ?? 'delivery') !== 'delivery') return undefined;
  if (shippingMode !== undefined) return shippingMode;
  // defaultChoice có thể là 'pickup' (không map được vào ShippingModeValue,
  // KHÔNG dùng được cho nhánh giao hàng đang xét) — fallback 'self' an toàn,
  // giữ đúng tinh thần "không tự đẩy job ĐVVC" khi cấu hình tenant không áp
  // dụng được cho ngữ cảnh này.
  return defaultChoice === 'self' || defaultChoice === 'carrier' ? defaultChoice : 'self';
}

/** Nhãn của từng lựa chọn, kể cả lựa chọn đã ẩn khỏi UI tạo mới. */
export const FULFILLMENT_LABEL: Record<FulfillmentChoice, string> = {
  pickup: 'Lấy tại chi nhánh',
  self: 'Tự vận chuyển',
  carrier: 'Giao cho ĐVVC',
  // Đã ẩn khỏi thanh chọn nhưng đơn cũ vẫn có thể mang giá trị này.
  vault: 'Lưu kho',
};

/**
 * Câu chữ hiển thị phương thức nhận hàng của một đơn ĐÃ LƯU.
 *
 * Vì sao phải qua đây thay vì `switch` trên `receive_method`: `self` và
 * `carrier` CÙNG lưu `receive_method: 'delivery'`, khác nhau ở `shipping_mode`.
 * Chỉ nhìn một field thì hai chế độ khác hẳn nhau dồn chung một nhãn "Giao
 * hàng" — người xem đơn không biết ai đang cầm hàng.
 *
 * Dùng lại `toChoice` để luật "giao hàng mà thiếu `shipping_mode` ⇒ tự vận
 * chuyển" chỉ tồn tại một chỗ. Luật này không phải chuyện lý thuyết: phần lớn
 * đơn giao hàng hiện có không hề lưu `shipping_mode`.
 */
export function describeFulfillment(fields: {
  receive_method?: ReceiveMethodValue | string | null;
  shipping_mode?: ShippingModeValue | string | null;
}): string {
  const method = fields.receive_method;
  if (method !== 'delivery' && method !== 'pickup' && method !== 'vault') {
    // Giá trị lạ (đơn nhập từ nguồn khác) — trả nguyên văn còn hơn nói sai.
    return typeof method === 'string' && method ? method : '---';
  }
  const mode =
    fields.shipping_mode === 'self' || fields.shipping_mode === 'carrier'
      ? fields.shipping_mode
      : undefined;
  return FULFILLMENT_LABEL[toChoice({ receive_method: method, shipping_mode: mode }, 'self')];
}
